import React, { useEffect, useRef, useState } from "react";
import { Eraser, Undo2 } from "lucide-react";

/**
 * Overlay <canvas> that lets the viewer draw red strokes on top of a photo.
 * The canvas is sized to match the rendered image (object-fit: contain) so the
 * exported PNG aligns 1:1 with the photo. Calls `onChange(dataUrl|null)`
 * whenever strokes change.
 *
 * Props:
 *   - imageRef      : ref to the underlying <img> element (so we can match its size)
 *   - active        : when true, captures pointer events; otherwise pointer-events-none
 *   - onChange      : (dataUrlOrNull) => void — fires on every commit/clear
 *   - color         : stroke color (default red)
 *   - lineWidth     : px (default 5)
 *   - resetSignal   : changing this value clears the canvas (used when user navigates photos)
 */
export default function PhotoAnnotator({
    imageRef,
    active,
    onChange,
    color = "#ef4444",
    lineWidth = 5,
    resetSignal = 0,
}) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const strokesRef = useRef([]); // array of arrays of {x, y} (each = one stroke)
    const currentStrokeRef = useRef(null);
    const [, force] = useState(0);

    // Resize canvas to match the rendered image's natural display size
    const syncSize = () => {
        const cnv = canvasRef.current;
        const img = imageRef?.current;
        if (!cnv || !img) return;
        const rect = img.getBoundingClientRect();
        const w = Math.max(1, Math.round(rect.width));
        const h = Math.max(1, Math.round(rect.height));
        // Use the device-pixel ratio so strokes stay crisp on retina screens
        const dpr = window.devicePixelRatio || 1;
        if (cnv.width !== w * dpr || cnv.height !== h * dpr) {
            cnv.width = w * dpr;
            cnv.height = h * dpr;
        }
        cnv.style.width = `${w}px`;
        cnv.style.height = `${h}px`;
        redraw();
    };

    const redraw = () => {
        const cnv = canvasRef.current;
        if (!cnv) return;
        const ctx = cnv.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cnv.width / dpr, cnv.height / dpr);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        const strokes = strokesRef.current;
        for (const s of strokes) {
            if (s.length < 1) continue;
            ctx.beginPath();
            ctx.moveTo(s[0].x, s[0].y);
            for (let i = 1; i < s.length; i++) ctx.lineTo(s[i].x, s[i].y);
            if (s.length === 1) {
                // dot — draw a tiny circle
                ctx.arc(s[0].x, s[0].y, lineWidth / 2, 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.fill();
            }
            ctx.stroke();
        }
    };

    // Watch image size changes (resize, photo nav, lightbox open)
    useEffect(() => {
        const img = imageRef?.current;
        if (!img) return;
        // If the image hasn't loaded yet, wait for it
        if (!img.complete) {
            const onLoad = () => syncSize();
            img.addEventListener("load", onLoad);
            return () => img.removeEventListener("load", onLoad);
        }
        syncSize();
        const ro = new ResizeObserver(() => syncSize());
        ro.observe(img);
        window.addEventListener("resize", syncSize);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", syncSize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageRef, resetSignal]);

    // Reset strokes when parent asks (e.g. when navigating to next photo)
    useEffect(() => {
        strokesRef.current = [];
        currentStrokeRef.current = null;
        redraw();
        onChange?.(null);
        force((x) => x + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetSignal]);

    const ptFromEvent = (e) => {
        const cnv = canvasRef.current;
        const rect = cnv.getBoundingClientRect();
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: cx - rect.left, y: cy - rect.top };
    };

    const start = (e) => {
        if (!active) return;
        e.preventDefault();
        drawingRef.current = true;
        currentStrokeRef.current = [ptFromEvent(e)];
        strokesRef.current.push(currentStrokeRef.current);
        redraw();
    };
    const move = (e) => {
        if (!drawingRef.current || !active) return;
        e.preventDefault();
        currentStrokeRef.current.push(ptFromEvent(e));
        redraw();
    };
    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        currentStrokeRef.current = null;
        commit();
    };

    const commit = () => {
        const cnv = canvasRef.current;
        if (!cnv) return;
        if (strokesRef.current.length === 0) {
            onChange?.(null);
            return;
        }
        onChange?.(cnv.toDataURL("image/png"));
        force((x) => x + 1);
    };

    const clear = () => {
        strokesRef.current = [];
        currentStrokeRef.current = null;
        redraw();
        onChange?.(null);
        force((x) => x + 1);
    };
    const undo = () => {
        strokesRef.current.pop();
        redraw();
        commit();
    };

    const hasStrokes = strokesRef.current.length > 0;

    return (
        <>
            <canvas
                ref={canvasRef}
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-0 left-0"
                style={{
                    cursor: active ? "crosshair" : "default",
                    pointerEvents: active ? "auto" : "none",
                    touchAction: active ? "none" : "auto",
                }}
                data-testid="photo-annotator-canvas"
            />

            {active && (
                <div
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-white/15 rounded-sm p-1.5 z-20"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="annotator-toolbar"
                >
                    <span
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white px-2 h-7"
                    >
                        <span className="h-3 w-3 rounded-full" style={{ background: color }} />
                        Rood
                    </span>
                    <div className="h-5 w-px bg-zinc-200" />
                    <button
                        type="button"
                        onClick={undo}
                        disabled={!hasStrokes}
                        className="inline-flex items-center justify-center h-7 px-2 text-xs text-zinc-900 hover:bg-zinc-100 rounded-sm disabled:opacity-40"
                        data-testid="annotator-undo-btn"
                        title="Laatste lijn ongedaan maken"
                    >
                        <Undo2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={clear}
                        disabled={!hasStrokes}
                        className="inline-flex items-center justify-center h-7 px-2 text-xs text-zinc-900 hover:bg-zinc-100 rounded-sm disabled:opacity-40"
                        data-testid="annotator-clear-btn"
                        title="Wis alles"
                    >
                        <Eraser className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </>
    );
}
